// src/components/SubcategoryBreakdown.js
import React, { useEffect, useMemo, useState } from "react";
import { SAT_STRUCTURE, normalize } from "../../../../utils/sat/satStructure";
import { computeSatBreakdown } from "../../../../utils/sat/satBreakdown";
import { loadSatBreakdownInputs } from "../../../../utils/sat/satBreakdownData";

// ---- Styles ----
const styles = {
  wrapper: {
    background: "#f9fafb",
    borderRadius: 16,
    padding: 16,
    border: "1px solid #e5e7eb",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    marginBottom: 12,
  },
  title: { fontSize: 15, fontWeight: 700, color: "#111827", margin: 0 },
  helper: { fontSize: 12, color: "#6b7280" },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: 16,
  },
  subjectCard: {
    background: "#ffffff",
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  subjectHeader: {
    padding: "10px 14px",
    borderBottom: "1px solid #e5e7eb",
    fontWeight: 700,
    fontSize: 14,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#111827",
    background: "#f3f4f6",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    tableLayout: "fixed",
  },
  th: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    textAlign: "left",
    color: "#6b7280",
    padding: "8px 10px",
    borderBottom: "1px solid #e5e7eb",
    background: "#f9fafb",
    whiteSpace: "nowrap",
  },
  td: {
    fontSize: 12,
    color: "#111827",
    padding: "6px 10px",
    borderBottom: "1px solid #f3f4f6",
    verticalAlign: "middle",
  },
  // Category row (unit)
  unitRow: {
    background: "#e5e7eb", // slightly darker so it's easy to see
    fontWeight: 700,
    fontSize: 13,
  },
  subRow: {},
  subName: {
    paddingLeft: 14,
  },
  dash: { color: "#d1d5db" },
  totalRow: {
    fontWeight: 700,
    background: "#f3f4f6",
    borderTop: "1px solid #e5e7eb",
  },
  totalLabel: {
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    fontSize: 11,
    color: "#4b5563",
  },
  empty: {
    fontSize: 13,
    color: "#6b7280",
    padding: "10px 0",
  },

  // accuracy "battery" UI (for categories only)
  accuracyCell: {
    display: "flex",
    flexDirection: "column",
    gap: 3,
    alignItems: "flex-end",
  },
  accuracyBadge: {
    fontSize: 11,
    fontWeight: 600,
    padding: "1px 8px",
    borderRadius: 999,
    display: "inline-block",
  },
  accuracyBarOuter: {
    width: 72,
    height: 4,
    borderRadius: 999,
    background: "#e5e7eb",
    overflow: "hidden",
  },
  accuracyBarInner: {
    height: "100%",
    borderRadius: 999,
  },
};

function accuracyColors(percentage) {
  if (percentage == null) {
    return {
      badgeBg: "#e5e7eb",
      badgeText: "#4b5563",
      barBg: "#9ca3af",
    };
  }
  if (percentage >= 80) {
    return {
      badgeBg: "#dcfce7",
      badgeText: "#166534",
      barBg: "#22c55e",
    };
  }
  if (percentage >= 60) {
    return {
      badgeBg: "#fef9c3",
      badgeText: "#92400e",
      barBg: "#facc15",
    };
  }
  return {
    badgeBg: "#fee2e2",
    badgeText: "#b91c1c",
    barBg: "#ef4444",
  };
}

// simple text color for subcategories
function accuracyTextColor(percentage) {
  if (percentage == null) return "#6b7280";
  if (percentage >= 80) return "#15803d";
  if (percentage >= 60) return "#b45309";
  return "#b91c1c";
}

function AccuracyCell({ percentage, total }) {
  if (total === 0 || percentage == null) {
    return <span style={styles.dash}>—</span>;
  }
  const { badgeBg, badgeText, barBg } = accuracyColors(percentage);
  const width = `${percentage}%`;

  return (
    <div style={styles.accuracyCell}>
      <span
        style={{
          ...styles.accuracyBadge,
          backgroundColor: badgeBg,
          color: badgeText,
        }}
      >
        {percentage}%
      </span>
      <div style={styles.accuracyBarOuter}>
        <div
          style={{
            ...styles.accuracyBarInner,
            width,
            backgroundColor: barBg,
          }}
        />
      </div>
    </div>
  );
}

export default function SubcategoryBreakdown({
  studentId,
  testId,
  attemptId,
  attemptLabel = "",
  diagnosticCollection = "sat_diagnostic_tests",
  answers: answersProp,
}) {
  const [loading, setLoading] = useState(true);
  const [studentAnswers, setStudentAnswers] = useState(null);
  const [qIndex, setQIndex] = useState(new Map());

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        const { questionIndex, answers } = await loadSatBreakdownInputs({
          testId,
          attemptId,
          studentId,
          answers: answersProp,
          diagnosticCollection,
        });

        if (!cancelled) {
          setQIndex(questionIndex);
          setStudentAnswers(answers || {});
        }
      } catch (e) {
        console.error("SubcategoryBreakdown fetch error:", e);
        if (!cancelled) {
          setQIndex(new Map());
          setStudentAnswers({});
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    if (!studentId || !testId) {
      setLoading(false);
      setQIndex(new Map());
      setStudentAnswers({});
      return;
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [studentId, testId, answersProp, attemptId, diagnosticCollection]);

  // Precompute per-sub, per-unit and per-subject metrics (shared pure logic)
  const { perSubMetrics, perUnitMetrics, subjectTotals } = useMemo(
    () => computeSatBreakdown(qIndex, studentAnswers),
    [qIndex, studentAnswers]
  );

  if (loading) return <div style={styles.empty}>Loading breakdown…</div>;

  return (
    <div style={styles.wrapper}>
      <div style={styles.cardHeader}>
        <h3 style={styles.title}>Subcategory Breakdown</h3>
        <span style={styles.helper}>
          RAW% and suggested Rx hours{" "}
          {attemptLabel ? `• ${attemptLabel}` : ""}
        </span>
      </div>

      <div style={styles.grid}>
        {Object.entries(SAT_STRUCTURE).map(([subjectName, units]) => {
          const subjectHours = subjectTotals[subjectName] || 0;

          return (
            <div key={subjectName} style={styles.subjectCard}>
              <div style={styles.subjectHeader}>{subjectName}</div>

              <table style={styles.table}>
                <colgroup>
                  <col style={{ width: "60%" }} />
                  <col style={{ width: "20%" }} />
                  <col style={{ width: "20%" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th style={styles.th}>Question Type</th>
                    <th style={{ ...styles.th, textAlign: "right" }}>RAW%</th>
                    <th style={{ ...styles.th, textAlign: "right" }}>Rx (hrs)</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(units).map(([unitName, subcats]) => {
                    const unitKey = `${subjectName}||${unitName}`;
                    const unit = perUnitMetrics[unitKey] || {};
                    const unitPct = unit.percentage;
                    const unitHours = unit.hours || 0;

                    return (
                      <React.Fragment key={unitName}>
                        {/* CATEGORY / UNIT ROW (battery-style accuracy + hours) */}
                        <tr style={styles.unitRow}>
                          <td style={styles.td}>{unitName}</td>
                          <td style={{ ...styles.td, textAlign: "right" }}>
                            <AccuracyCell
                              percentage={unitPct}
                              total={unit.total || 0}
                            />
                          </td>
                          <td
                            style={{
                              ...styles.td,
                              textAlign: "right",
                              fontWeight: 700,
                            }}
                          >
                            {unitHours > 0 ? unitHours : <span style={styles.dash}>—</span>}
                          </td>
                        </tr>

                        {/* SUBCATEGORY rows – simple colored % text */}
                        {subcats.map((subName) => {
                          const keyNorm = normalize(subName);
                          const metrics = perSubMetrics[keyNorm] || {};
                          const { total, percentage, hours } = metrics;
                          const color = accuracyTextColor(percentage);

                          return (
                            <tr key={subName} style={styles.subRow}>
                              <td style={{ ...styles.td, ...styles.subName }}>
                                {subName}
                              </td>
                              <td
                                style={{
                                  ...styles.td,
                                  textAlign: "right",
                                  color,
                                  fontWeight:
                                    total === 0 || percentage == null ? 400 : 600,
                                }}
                              >
                                {total === 0 || percentage == null ? (
                                  <span style={styles.dash}>—</span>
                                ) : (
                                  `${percentage}%`
                                )}
                              </td>
                              <td
                                style={{
                                  ...styles.td,
                                  textAlign: "right",
                                  fontWeight: 600,
                                }}
                              >
                                {hours == null ? (
                                  <span style={styles.dash}>—</span>
                                ) : (
                                  hours
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}

                  {/* SUBJECT total row */}
                  <tr style={styles.totalRow}>
                    <td style={{ ...styles.td }}>
                      <span style={styles.totalLabel}>TOTAL</span>
                    </td>
                    <td
                      style={{
                        ...styles.td,
                        textAlign: "right",
                        color: "#6b7280",
                      }}
                    >
                      <span style={styles.dash}>—</span>
                    </td>
                    <td
                      style={{
                        ...styles.td,
                        textAlign: "right",
                        fontWeight: 700,
                      }}
                    >
                      {subjectHours > 0 ? `${subjectHours} hrs` : "0 hrs"}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          );
        })}
      </div>
    </div>
  );
}
